// ============================================================
// MySQLStoredProceduresAndTriggers.jsx
//
// Stored Procedures & Triggers (MySQL)
// Foundation · Module 08
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Collapse,
    Paper,
} from "@mui/material";

/* Icons */
import MenuBookIcon from "@mui/icons-material/MenuBook";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import CodeIcon from "@mui/icons-material/Code";
import BuildIcon from "@mui/icons-material/Build";
import BoltIcon from "@mui/icons-material/Bolt";
import SecurityIcon from "@mui/icons-material/Security";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import SchoolIcon from "@mui/icons-material/School";
import StarIcon from "@mui/icons-material/Star";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#100720",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold: "#DAA520",
    goldBright: "#FFD700",
    goldGlow: "rgba(218,165,32,0.15)",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    codeBg: "rgba(0,0,0,0.45)",
    codeText: "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
};

/* ───────────────── SUB COMPONENTS ───────────────── */

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box
                sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    background: BRAND.goldGlow,
                    border: `1px solid ${BRAND.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>

            <Typography variant="h6" sx={{ ...sectionHeading }}>
                {label}
            </Typography>
        </Box>
    );
}

function ConceptCard({ icon, title, description }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                height: "100%",
                background: BRAND.surface,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 3,
                "&:hover": {
                    borderColor: BRAND.borderHover,
                    boxShadow: `0 0 18px ${BRAND.goldGlow}`,
                },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold } })}
                <Typography sx={{ fontWeight: 700, color: BRAND.white }}>
                    {title}
                </Typography>
            </Box>

            <Typography sx={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.7 }}>
                {description}
            </Typography>
        </Paper>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const copy = () => {
        navigator.clipboard.writeText(fullText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box
            sx={{
                background: BRAND.codeBg,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2,
                mb: 3,
            }}
        >
            <Box
                sx={{
                    px: 2,
                    py: 1,
                    display: "flex",
                    justifyContent: "space-between",
                }}
            >
                <Box sx={{ display: "flex", gap: 1 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box
                            key={c}
                            sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                background: c,
                            }}
                        />
                    ))}
                </Box>

                <Tooltip title={copied ? "Copied!" : "Copy"}>
                    <Box onClick={copy} sx={{ cursor: "pointer", color: BRAND.gold }}>
                        {copied ? <DoneIcon /> : <ContentCopyIcon />}
                    </Box>
                </Tooltip>
            </Box>

            <Box sx={{ p: 2 }}>
                {lines.map((l, i) => (
                    <Box key={i} sx={{ mb: 1 }}>
                        {l.comment && (
                            <Typography
                                sx={{
                                    fontFamily: "monospace",
                                    fontSize: 12,
                                    color: BRAND.muted,
                                }}
                            >
                                -- {l.comment}
                            </Typography>
                        )}
                        <Typography sx={{ fontFamily: "monospace", color: BRAND.codeText }}>
                            {l.cmd}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

function ExpandableExample({ title, icon, children }) {
    const [open, setOpen] = useState(false);
    return (
        <Box sx={{ border: `1px solid ${BRAND.border}`, borderRadius: 2, mb: 2 }}>
            <Box
                onClick={() => setOpen(!open)}
                sx={{
                    p: 2,
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    background: BRAND.surface,
                }}
            >
                <Box sx={{ display: "flex", gap: 1 }}>
                    {React.cloneElement(icon, { sx: { color: BRAND.gold } })}
                    <Typography sx={{ color: BRAND.white }}>{title}</Typography>
                </Box>

                {open ? (
                    <ExpandLessIcon sx={{ color: BRAND.gold }} />
                ) : (
                    <ExpandMoreIcon sx={{ color: BRAND.muted }} />
                )}
            </Box>

            <Collapse in={open}>
                <Box sx={{ p: 2, background: "rgba(0,0,0,0.25)" }}>{children}</Box>
            </Collapse>
        </Box>
    );
}

/* ───────────────── MAIN PAGE ───────────────── */

export default function MySQLStoredProceduresAndTriggers() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: 6 }}>
            <Container maxWidth="lg">
                {/* HERO */}
                <Chip
                    icon={<SchoolIcon sx={{ color: BRAND.gold }} />}
                    label="Foundation · Module 08"
                    sx={{ mb: 2, background: BRAND.goldGlow, color: BRAND.gold }}
                />

                <Typography variant="h3" sx={{ ...sectionHeading, mb: 2 }}>
                    Stored Procedures & <span style={{ color: BRAND.gold }}>Triggers</span>
                </Typography>

                <Typography sx={{ color: BRAND.muted, maxWidth: 780 }}>
                    Stored procedures centralize business logic inside the database.
                    Triggers react automatically to data changes. Used wisely, they enforce
                    consistency and reduce application complexity.
                </Typography>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* WHY */}
                <SectionTitle
                    icon={<LightbulbIcon />}
                    label="Why Stored Logic Exists"
                />
                <Typography sx={{ color: BRAND.muted, mb: 3 }}>
                    Some rules must always run — regardless of which application touches
                    the database. Stored procedures and triggers enforce those rules
                    directly at the database level.
                </Typography>

                {/* KEY CONCEPTS */}
                <SectionTitle icon={<MenuBookIcon />} label="Core Concepts" />
                <Grid container spacing={2}>
                    {[
                        {
                            icon: <BuildIcon />,
                            title: "Stored Procedures",
                            description:
                                "Reusable SQL programs that accept parameters and execute logic on demand.",
                        },
                        {
                            icon: <BoltIcon />,
                            title: "Triggers",
                            description:
                                "Automatic logic executed BEFORE or AFTER INSERT, UPDATE, or DELETE.",
                        },
                        {
                            icon: <SyncAltIcon />,
                            title: "Event Timing",
                            description:
                                "Triggers run per row and can enforce rules instantly.",
                        },
                        {
                            icon: <SecurityIcon />,
                            title: "Data Integrity",
                            description:
                                "Triggers protect against invalid or inconsistent data changes.",
                        },
                        {
                            icon: <CodeIcon />,
                            title: "Application Balance",
                            description:
                                "Not all logic belongs in the database — choose carefully.",
                        },
                    ].map((c) => (
                        <Grid item xs={12} sm={6} md={4} key={c.title}>
                            <ConceptCard {...c} />
                        </Grid>
                    ))}
                </Grid>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* STORED PROCEDURES */}
                <SectionTitle icon={<CodeIcon />} label="Hands‑On: Stored Procedures" />

                <CodeBlock
                    lines={[
                        {
                            comment: "Create a stored procedure",
                            cmd: `DELIMITER $$

CREATE PROCEDURE get_student_by_id(IN studentId INT)
BEGIN
  SELECT * FROM students WHERE id = studentId;
END$$

DELIMITER ;`,
                        },
                        { comment: "Call the procedure", cmd: "CALL get_student_by_id(1);" },
                    ]}
                />

                <ExpandableExample
                    title="Procedure with Business Logic"
                    icon={<BuildIcon />}
                >
                    <CodeBlock
                        lines={[
                            {
                                comment: "Transfer funds safely",
                                cmd: `DELIMITER $$

CREATE PROCEDURE transfer_funds(
  IN from_account INT,
  IN to_account INT,
  IN amount DECIMAL(10,2)
)
BEGIN
  START TRANSACTION;

  UPDATE accounts SET balance = balance - amount WHERE id = from_account;
  UPDATE accounts SET balance = balance + amount WHERE id = to_account;

  COMMIT;
END$$

DELIMITER ;`,
                            },
                        ]}
                    />
                </ExpandableExample>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* TRIGGERS */}
                <SectionTitle icon={<BoltIcon />} label="Hands‑On: Triggers" />

                <CodeBlock
                    lines={[
                        {
                            comment: "Prevent negative balances",
                            cmd: `DELIMITER $$

CREATE TRIGGER before_account_update
BEFORE UPDATE ON accounts
FOR EACH ROW
BEGIN
  IF NEW.balance < 0 THEN
    SIGNAL SQLSTATE '45000'
    SET MESSAGE_TEXT = 'Balance cannot be negative';
  END IF;
END$$

DELIMITER ;`,
                        },
                    ]}
                />

                <ExpandableExample title="Audit Log Trigger" icon={<SecurityIcon />}>
                    <CodeBlock
                        lines={[
                            {
                                comment: "Log changes automatically",
                                cmd: `CREATE TABLE audit_log (
  id INT AUTO_INCREMENT PRIMARY KEY,
  table_name VARCHAR(50),
  action VARCHAR(10),
  changed_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP
);

DELIMITER $$

CREATE TRIGGER after_student_insert
AFTER INSERT ON students
FOR EACH ROW
BEGIN
  INSERT INTO audit_log (table_name, action)
  VALUES ('students', 'INSERT');
END$$

DELIMITER ;`,
                            },
                        ]}
                    />
                </ExpandableExample>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* LEARNED */}
                <SectionTitle icon={<StarIcon />} label="What You've Learned" />
                <Grid container spacing={2}>
                    {[
                        "What stored procedures are and when to use them",
                        "How to pass parameters to procedures",
                        "How triggers execute automatically",
                        "Difference between BEFORE and AFTER triggers",
                        "How triggers protect data integrity",
                        "Why overusing triggers can be dangerous",
                    ].map((i) => (
                        <Grid item xs={12} sm={6} key={i}>
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <CheckCircleOutlineIcon sx={{ color: BRAND.gold }} />
                                <Typography sx={{ color: BRAND.muted }}>{i}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* FOOTER */}
                <Box sx={{ p: 3, background: BRAND.goldGlow, borderRadius: 2 }}>
                    <Typography sx={{ color: BRAND.white }}>
                        💡 <strong>Pro Tip:</strong> Use stored procedures for business rules
                        and triggers for data safety. If debugging becomes hard, the logic may
                        belong in the application instead.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
}

